/**
 * The BUBBLE stream's own state (spec 02 §4). Per bubble: the columns of the C64's software-entity
 * table the port runs on (rebb64 names in brackets). Per world: what the C64 keeps in zero page for
 * the bubbles - the chain counters, the hole spawner, the water stream (see `system.ts`). Plain
 * numbers and booleans only, so the world stays serialisable (06 R5).
 *
 * The bubble's column and sub-column are not stored: the C64 moves [$DC,x] and [$A9C4,x] in step with
 * X by even amounts, so they are always ((X - $14) >> 3) and ((X - $14) >> 1) & 3 (`colOf`, `subXOf`).
 * The row is stored: it can part from Y (README.md).
 */

import type { Facing, PlayerId } from '../types';

export interface BubbleMotion {
  /** M-ticks since the bubble was made. */
  age: number;
  /** Software-sprite slot 0..17 [x]: the C64 runs every loop from slot 17 down, so order matters. */
  slot: number;
  /** The C64 entity type [$CA,x] (codes.ts). */
  code: number;
  /** The C64's bubble row [$EE,x]: tile row + 3 of its top when `subY` is 0 (README.md). */
  row: number;
  /** Pixels into the row [$A9D6,x]: 0, 2, 4, 6; non-zero means the bubble's top is in row - 1. */
  subY: number;
  /** Shot steps left [$A9B2,x], bit 7 marking the first; 0 once it floats. */
  steps: number;
  /** Life, or escape, timer [$A9FA,x], counted down every M-tick. */
  timer: number;
  /** The type saved when it bursts [$AA42,x]. */
  saved: number;
  /** The shot's direction [$0193,x bit 7 during the shot]: 1 right, -1 left. */
  dir: 1 | -1;
  /** Who a burst is credited to [$0193,x once touched or chained]. */
  credit: PlayerId | -1;
  /** Blue candy: two shot steps per M-tick [$AA42,x bit 7 during the shot] (M2). */
  fast: boolean;
  /** Purple candy: the grow index is halved [$AA30,x bit 7] (M2). */
  halfGrow: boolean;
  /** Burst frame for the view: -1 none; 0, 1 the first burst picture; 2, 3 the second; 4 gone. */
  burst: number;
  /** Trapped, in its last 16 M-ticks: the view flashes it (with `visible`). */
  flash: boolean;
  /** Trapped and flashed off (the C64 toggles the monster sprite's enable bit, L142E). */
  flashOff: boolean;
}

export function initialBubbleMotion(): BubbleMotion {
  return {
    age: 0, slot: 0, code: 0x04, row: 0, subY: 0, steps: 0, timer: 0, saved: 0, dir: 1, credit: -1,
    fast: false, halfGrow: false, burst: -1, flash: false, flashOff: false,
  };
}

/** The water stream (02 §8): the C64's $A74B / $A756 cell arrays and $4D. */
export interface WaterState {
  /** Tile column of each cell, cell 0 the tail, cell 10 the head. */
  cols: number[];
  /** C64 row of each cell: tile row + 4; $1D is past the bottom. */
  rows: number[];
  /** $4D: -1 falling; 4 flowing left, 5 flowing right. */
  mode: number;
  /** Monsters being carried (their state is 'trapped', bubbleId -1, while carried). */
  monsters: number[];
  /** Players being carried. */
  players: PlayerId[];
}

/** The bubble system's per-world state. */
export interface BubbleSystem {
  /** Monsters killed in each player's running chain [$46,x] and at the start of this pass [$48,x]. */
  chain: [number, number];
  chainSeen: [number, number];
  /** The hole spawner is on (EXTEND flag: the JSR at $0A38), its timer [$0BEE], its specials [$0CC2]. */
  spawnOn: boolean;
  spawnTimer: number;
  spawnMask: number;
  /** Letter bubbles still to come from the holes [$4C + 1, while $0C9C is BIT] and the letter seed [$5BFF]. */
  letters: number;
  letterSeed: number;
  /** The level-clear sweep has run ($16F7 once $4A reaches 0). */
  cleared: boolean;
  water: WaterState | null;
  /** Fire drops falling (type $24), the five burning lines (D_A761..D_A770), bolts in flight ($26, $44). */
  drops: FireDrop[];
  fires: FireLine[];
  bolts: Bolt[];
  /** EXTEND's screenful of food has been laid out this round. */
  extendFood: boolean;
}

/**
 * A burst fire bubble's drop (02 §8), the C64's type $24 (L11B7): it keeps the bubble's slot, X and
 * row; the sub-row toggles 0 / 4 as it falls, the row stepping on the 4.
 */
export interface FireDrop {
  slot: number;
  /** VIC X, the bubble's (drawn with its sub-column, as a bubble). */
  x: number;
  row: number;
  subY: number;
  /** Who burst it (the C64 credits nobody for what it burns). */
  credit: PlayerId | -1;
}

/**
 * A burning line, one of five (D_1319): its leftmost tile column [D_A761], the drop's bubble row
 * [D_A766] (it burns on tile row - 4, over the platform whose top is tile row - 3), its length in
 * cells [D_A76B], and its timer [D_A770]: 6 when lit, one down each pass, -1 out.
 */
export interface FireLine {
  col: number;
  row: number;
  len: number;
  timer: number;
  credit: PlayerId | -1;
}

/**
 * A bolt (02 §7.5, §8): a burst lightning bubble's (type $26) or a red-cross shot (type $44). X, Y as
 * the C64 compares them with the monsters [D_AA0C, D_AA1E]; column and row as it draws it [$DC, $EE,
 * D_A9D6]. It moves 8 px (one column) a pass.
 */
export interface Bolt {
  slot: number;
  x: number;
  y: number;
  col: number;
  row: number;
  subY: number;
  dir: Facing;
  /** Who set it off (-1 nobody). */
  player: PlayerId | -1;
  redCross: boolean;
}

/** A bubble turned into 700-point food at a bonus clear (type $4C, L0EC5): its slot and C64 cell. */
export interface FoodFall {
  slot: number;
  row: number;
  subY: number;
}
